import { useMemo } from 'react';
import { CalendarDayProps, CalendarDayStyle } from '../types/Calendar';
import { EnumTypeEvent, EventsInterface } from '../service/Business/Interfaces/CalendarInterface';
import { AppSettings } from '../AppSettings';

const COLORS = AppSettings.Freelancerie.Calendar;

const getEmptyStyle = (): CalendarDayStyle => ({
    style: { backgroundColor: "", color: "", cursor: "default" },
    tooltip1: "",
    tooltip2: "",
    dataPeriod: undefined
});

const getCurrentDayStyle = (): CalendarDayStyle => ({
    style: {
        backgroundColor: COLORS.COLOR_CURRENT_DAY,
        color: "#ffffff",
        cursor: "default"
    },
    tooltip1: "Aujourd'hui",
    tooltip2: "",
    dataPeriod: undefined
});

const getPriorityColor = (currentColor: string, newEventColor: string): string => {
    const priorityOrder = [
        COLORS.COLOR_CRITIC,
        COLORS.COLOR_URSSAF,
        COLORS.COLOR_FERIE,
        COLORS.COLOR_WORKED,
        COLORS.COLOR_WEEKEND,
        COLORS.COLOR_HOLIDAY
    ];

    const currentPriority = priorityOrder.indexOf(currentColor);
    const newPriority = priorityOrder.indexOf(newEventColor);

    return currentPriority > -1 && currentPriority < newPriority
        ? currentColor
        : newEventColor;
};

const getWeekendStyle = (event: EventsInterface, currentStyle: CalendarDayStyle, editable: boolean): CalendarDayStyle => {
    const criticalColors = [COLORS.COLOR_CRITIC, COLORS.COLOR_FERIE, COLORS.COLOR_URSSAF];
    const eventColor = getColorFromEvent(event);
    const backgroundColor = criticalColors.includes(currentStyle.style.backgroundColor)
        ? currentStyle.style.backgroundColor
        : criticalColors.includes(eventColor)
            ? eventColor
            : COLORS.COLOR_WEEKEND;

    return {
        ...currentStyle,
        style: {
            backgroundColor,
            color: "#ffffff",
            cursor: editable ? "pointer" : "default"
        }
    };
};

const mergeEventStyle = (event: EventsInterface, currentStyle: CalendarDayStyle, editable: boolean): CalendarDayStyle => ({
    style: {
        backgroundColor: getPriorityColor(currentStyle.style.backgroundColor, getColorFromEvent(event)),
        color: "#ffffff",
        cursor: editable ? "pointer" : "default"
    },
    tooltip1: `${event.title} ${currentStyle.tooltip1}`,
    tooltip2: `${event.description} ${currentStyle.tooltip2}`,
    dataPeriod: event.period
});

const calculateEventStyle = (date: Date, events: EventsInterface[], editable: boolean): CalendarDayStyle => {
    let style = getEmptyStyle();
    let hasEvent = false;

    for (const event of events) {
        const begin = new Date(event.begin);
        const end = new Date(event.end);

        // Normalisation des dates pour comparer uniquement les jours
        const compareDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
        const compareBegin = new Date(begin.getUTCFullYear(), begin.getUTCMonth(), begin.getUTCDate());
        const compareEnd = new Date(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());

        if (compareDate >= compareBegin && compareDate <= compareEnd) {
            hasEvent = true;

            if (date.getDay() === 0 || date.getDay() === 6) {
                style = getWeekendStyle(event, style, editable);
            }

            style = mergeEventStyle(event, style, editable);
        }
    }

    if (!hasEvent && (date.getDay() === 0 || date.getDay() === 6)) {
        return {
            style: {
                backgroundColor: COLORS.COLOR_WEEKEND,
                color: "#ffffff",
                cursor: editable ? "pointer" : "default"
            },
            tooltip1: "Weekend !",
            tooltip2: "",
            dataPeriod: undefined
        };
    }

    return style;
};

export const useCalendarDayStyle = (props: CalendarDayProps): CalendarDayStyle => {
    return useMemo(() => {
        if (!props.date) return getEmptyStyle();
        if (isSameDay(new Date(), props.date)) return getCurrentDayStyle();
        return calculateEventStyle(props.date, props.datasBDD.listEvents, props.editable);
    }, [props.date, props.datasBDD.listEvents, props.editable]);
};

function isSameDay(d1: Date, d2: Date): boolean {
    return (d1.getDate() === d2.getDate() &&
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth())
}

function getColorFromEvent(e: EventsInterface) {
    switch (e.type) {
        case EnumTypeEvent.CRITIC:
            return AppSettings.Freelancerie.Calendar.COLOR_CRITIC;
        case EnumTypeEvent.FERIE:
            return AppSettings.Freelancerie.Calendar.COLOR_FERIE;
        case EnumTypeEvent.HOLIDAY:
            return AppSettings.Freelancerie.Calendar.COLOR_HOLIDAY
        case EnumTypeEvent.WORKED:
            return AppSettings.Freelancerie.Calendar.COLOR_WORKED;
        case EnumTypeEvent.WEEKEND:
            return AppSettings.Freelancerie.Calendar.COLOR_WEEKEND;
        case EnumTypeEvent.URSSAF:
            return AppSettings.Freelancerie.Calendar.COLOR_URSSAF;

        default:
            return "";
    }
}